import React from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch } from 'hooks';
import {
  Box,
  UnorderedList,
  ListItem,
  useToast,
  ToastId,
} from '@chakra-ui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { AnyObjectSchema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { formContext } from './Form.Context';
import { useTranslation } from 'react-i18next';

const ERROR_CODES: { [key: string]: string } = {
  400: 'Bad Request',
  401: 'Unauthorized',
  402: 'Payment Required',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

export interface FormHandle {
  submitForm(): void;
}

export interface FormProps {
  messageOnSuccess?: string;
  children?: React.ReactNode;
  defaultValues?: Record<string, any>;
  schema?: AnyObjectSchema;
  permissionsToEdit?: string[];
  operationType?: 'create' | 'update';
  context?: Record<string, any>;
  diff?: Record<string, any>;
  dispatchMethod?: (values: Record<string, any>) => any;
  onSubmit?: (values: Record<string, any>, resetForm?: () => void) => void;
  [key: string]: any;
  disableAll?: boolean;
  id?: string;
  reference?: React.RefObject<FormHandle>;
  resetOnDefaultValuesChange?: boolean;
  submitMode?: 'redux' | 'query';
  mutation?: (values?: any) => void | Promise<unknown>;
}

export interface DiffProps {
  field: string;
  old: string | number | boolean | string[] | number[] | boolean[];
  new: string | number | boolean | string[] | number[] | boolean[];
}

interface UiError {
  key: string;
  message: string;
}

function handleReactHookFormErrors(
  array: Array<[string, Record<string, any>]>,
): UiError[] {
  const errors: UiError[] = [];
  array.forEach(([, errorObject]) => {
    errors.push(...handleErrorObject(errorObject));
  });
  return errors;
}

function handleErrorObject(errorObject: Record<string, any>): UiError[] {
  if ('message' in errorObject) {
    return [{ key: errorObject?.ref?.name, message: errorObject.message }];
  }
  const errors: UiError[] = [];
  for (const key in errorObject) {
    errors.push(...handleErrorObject(errorObject[key]));
  }

  return errors;
}

function handleErrorMessageFromApi(obj: { [key: string]: any }) {
  const res: { [key: string]: string } = {};
  function recurse(obj: { [key: string]: any }, current?: string) {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const newKey = current ? current + '.' + key : key;
      if (
        value &&
        typeof value === 'object' &&
        !(value instanceof Date) &&
        !Array.isArray(value)
      ) {
        recurse(value, newKey);
      } else {
        res[newKey] = value;
      }
    });
  }
  recurse(obj);
  return res;
}

/**
 * Provider dla formularzy w oparciu o react-hook-form i useForm
 * @see https://react-hook-form.com/api/useform
 */
export const Form = ({
  messageOnSuccess,
  dispatchMethod,
  onSubmit,
  children,
  defaultValues,
  schema,
  permissionsToEdit = [],
  context,
  diff,
  disableAll,
  id,
  reference,
  resetOnDefaultValuesChange = false,
  submitMode = 'redux',
  mutation,
  ...props
}: FormProps): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast();
  const toastIdRef = React.useRef<ToastId | undefined>();
  //Flag for differentiating RHF validation from API errors
  const isFrontendValidationToastRef = React.useRef(false);
  const dispatch = useAppDispatch();
  const reactHookForm = useForm({
    ...props,
    resolver: schema && yupResolver(schema),
    defaultValues,
  });

  function showErrorAsToast(
    title: string,
    description?: string | React.ReactNode,
  ): void {
    hideToast();
    toastIdRef.current = toast({
      title,
      description,
      position: 'bottom',
      status: 'error',
      isClosable: true,
      duration: 5000,
    });
  }

  function hideToast(): void {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
      toastIdRef.current = undefined;
    }
  }

  function showSuccessAsToast(): void {
    isFrontendValidationToastRef.current = false;
    hideToast();

    toast({
      title: t('success', 'Sukces'),
      description: messageOnSuccess,
      position: 'bottom',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }

  function handleErrorFromApi(response: any): void {
    isFrontendValidationToastRef.current = false;

    const { status } = response;

    if (!status) {
      showErrorAsToast(t('form.errorUnknown', 'Coś poszło nie tak'));
      return;
    }
    switch (status) {
      case 400: {
        const message = handleErrorMessageFromApi(response.data.message);
        showErrorAsToast(
          typeof message === 'string'
            ? message
            : t('formContainsErrors', 'Formularz zawiera błędy'),
        );
        if (typeof message === 'object') {
          Object.keys(message).forEach(key => {
            reactHookForm.setError(key, { message: message[key] });
          });
        }
        break;
      }
      default:
        showErrorAsToast(`${status}: ${ERROR_CODES[status]}`);
        break;
    }
  }

  React.useImperativeHandle(
    reference,
    (): FormHandle => ({
      submitForm() {
        reactHookForm.handleSubmit(
          submitMode === 'redux' ? handleSubmit : handleQuerySubmit,
        )();
      },
    }),
  );

  async function handleSubmit(values: Record<string, any>): Promise<any> {
    try {
      let response: Record<string, any>;
      if (dispatchMethod) {
        response = unwrapResult(
          await dispatch(dispatchMethod(values)),
        ) as Record<string, any>;
      } else {
        //zwróć wartości formularza jeśli nie podano dispatchMethod (do onSubmit)
        response = values;
      }

      if (typeof onSubmit === 'function') onSubmit(response, resetForm);
      showSuccessAsToast();
    } catch (error) {
      handleErrorFromApi(error);
    }
  }

  async function handleQuerySubmit(values: Record<string, any>): Promise<any> {
    try {
      const response = (mutation ? await mutation(values) : values) as Record<
        string,
        any
      >;
      if (typeof onSubmit === 'function') onSubmit(response, resetForm);
      showSuccessAsToast();
    } catch (error) {
      handleErrorFromApi(error);
    }
  }

  function resetForm() {
    reactHookForm.reset(defaultValues);
  }

  function checkFiledDiff(name: string): DiffProps | undefined {
    if (!diff) return undefined;
    return diff.find((item: DiffProps) => item.field === name);
  }

  //effect needed when defaultValues are changing (e.g. when switching between counter-offer and original proposal)
  React.useEffect(() => {
    if (resetOnDefaultValuesChange && defaultValues) {
      reactHookForm.reset(defaultValues);
    }
  }, [defaultValues, resetOnDefaultValuesChange, reactHookForm.reset]);

  React.useEffect(() => {
    const errors = Object.entries(reactHookForm.formState.errors);
    const submitCount = reactHookForm.formState.submitCount;

    if (submitCount > 0 && errors.length) {
      const errorDescription = (
        <UnorderedList>
          {handleReactHookFormErrors(errors).map(({ key, message }, index) => (
            <ListItem key={key || index}>{t(message, message)}</ListItem>
          ))}
        </UnorderedList>
      );
      showErrorAsToast('Formularz zawiera błędy', errorDescription);
      isFrontendValidationToastRef.current = true;
    } else {
      if (isFrontendValidationToastRef.current) {
        hideToast();
        isFrontendValidationToastRef.current = false;
      }
    }
  }, [reactHookForm.formState.errors, reactHookForm.formState.submitCount]);

  React.useEffect(() => {
    return () => {
      hideToast();
    };
  }, []);

  return (
    <formContext.Provider
      value={{
        ...context,
        diff,
        checkFiledDiff,
        permissionsToEdit,
        defaultValues,
        resetForm,
        disableAll,
      }}
    >
      <FormProvider {...reactHookForm}>
        <Box
          as='form'
          onSubmit={reactHookForm.handleSubmit(
            submitMode === 'redux' ? handleSubmit : handleQuerySubmit,
          )}
          id={id}
        >
          {children}
        </Box>
      </FormProvider>
    </formContext.Provider>
  );
};
